import { generateClient } from "aws-amplify/api";
import { downloadData } from "aws-amplify/storage";

import { forecastRecordsByOutputParam } from "../graphql/customQueries";

import { saveBlob } from "./fileUtils";
import { outputFileName } from "./runHistory";
import { parseStandardTimeSeriesCsv, standardTimeSeriesToForecastPayload } from "./standardTimeSeriesCsv";

export interface ParsedForecastData {
  parsedData: any;
  modelKeys: string[];
  defaultModelKey: string;
}

export interface VariationOption {
  key: string;
  label: string;
}

/**
 * Parses a ForecastRecord file body into the forecast JSON shape `{forecast_issued, models}`.
 * `.csv` keys are Standard time-series CSV (docs/architecture/STANDARD_TIMESERIES_CSV.md); anything else is JSON.
 */
export const parseForecastFileText = (text: string, s3Key: string): any =>
  /\.csv$/i.test(s3Key)
    ? standardTimeSeriesToForecastPayload(parseStandardTimeSeriesCsv(text))
    : JSON.parse(text);

/**
 * Fetches a ForecastRecord file (JSON or Standard time-series CSV) from S3 and parses it.
 * Reuses the same S3 download pattern as ProcessModelDownload.tsx
 */
export const fetchAndParseForecastRecord = async (
  s3Key: string
): Promise<ParsedForecastData> => {
  const downloadResult = await downloadData({ path: s3Key }).result;
  const text = await downloadResult.body.text();
  const parsedData = parseForecastFileText(text, s3Key);

  const models = parsedData?.models || {};
  const modelKeys = Object.keys(models);
  const defaultModelKey = modelKeys[0] || "";

  return { parsedData, modelKeys, defaultModelKey };
};

/** Save a model output file (e.g. a run's CSV) as-is under its own file name. Reads through Amplify storage, the same path the preview reads, so anyone who can see the chart can save its file. */
export const downloadModelOutputFile = async (s3Key: string): Promise<void> => {
  const { body } = await downloadData({ path: s3Key }).result;
  saveBlob(await body.blob(), outputFileName(s3Key));
};

/**
 * Extracts variation options from parsed forecast JSON.
 * Same logic as ProcessModelDownload lines 383-389.
 */
export const getVariationOptions = (parsedData: any): VariationOption[] => {
  const models = parsedData?.models || {};
  return Object.keys(models).map((key) => ({
    key,
    label: models[key]?.variation ?? models[key]?.displayName ?? key,
  }));
};

export interface TemplateOutputParam {
  fieldKey: string;
  name: string;
  parameterTypeId: string;
}

/** Minimal shape of a field definition needed for output classification. */
export interface FieldDefinitionLike {
  section?: string;
  [key: string]: any;
}

/** Minimal shape of a template/schema needed to derive its output params. */
export interface TemplateLike {
  id?: string;
  fieldDefinitions?: string | Record<string, FieldDefinitionLike>;
}

/**
 * True if a field definition represents an output parameter.
 *
 * Output fields are authored in the admin Model Garden with keys prefixed
 * `output-` and `section: "output"` (see Admin/ModelGarden.tsx, key = `output-${id}`).
 * This is the single source of truth for output classification — use it everywhere
 * instead of an ad-hoc `key.startsWith('output')`, which would misclassify a regular
 * field named e.g. `outputFormat`.
 */
export const isOutputFieldKey = (
  fieldKey: string,
  field?: FieldDefinitionLike
): boolean => {
  const section = field?.section;
  return section === "output" || section === "outputs" || fieldKey.startsWith("output-");
};

/**
 * The deterministic cross-model `parameterTypeId` for an output field:
 * `{templateId}-{kebabCaseName}`, where the name is the field key with its
 * `output` prefix stripped. Single source of truth for this derivation.
 */
export const outputParameterTypeId = (fieldKey: string, typeId: string): string => {
  const name = fieldKey.replace(/^output[-_]?/i, "");
  const kebabCaseName = name.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
  return `${typeId}-${kebabCaseName}`;
};

/**
 * Derives the output parameterTypeIds a model schema template will produce.
 *
 * Accepts a template whose `fieldDefinitions` is either a JSON string (as stored)
 * or an already-parsed object.
 */
export const getTemplateOutputParams = (
  template: TemplateLike | null | undefined
): TemplateOutputParam[] => {
  if (!template) return [];

  let fieldDefinitions: Record<string, FieldDefinitionLike> = {};
  try {
    fieldDefinitions =
      typeof template.fieldDefinitions === "string"
        ? JSON.parse(template.fieldDefinitions || "{}")
        : template.fieldDefinitions || {};
  } catch {
    return [];
  }

  const typeId = template.id || "";
  const result: TemplateOutputParam[] = [];
  Object.entries(fieldDefinitions).forEach(([fieldKey, field]) => {
    if (!isOutputFieldKey(fieldKey, field)) return;
    const name = fieldKey.replace(/^output[-_]?/i, "");
    result.push({ fieldKey, name, parameterTypeId: outputParameterTypeId(fieldKey, typeId) });
  });
  return result;
};

/** Fetches the next page of an output's ForecastRecords, newest first. */
export const fetchForecastRecordsPage = async (
  parameterId: string,
  nextToken: string
): Promise<{ items: any[]; nextToken: string | null }> => {
  const result: any = await generateClient().graphql({
    query: forecastRecordsByOutputParam,
    variables: { parameterId, sortDirection: "DESC", limit: 20, nextToken },
  });
  const page = result.data.forecastRecordsByParameterIdAndForecastIssued;
  return { items: page.items || [], nextToken: page.nextToken ?? null };
};
